import { useRequest } from 'features/request/useRequest';
import { useResetMultipleStoreData } from 'features/request/useResetStoreData';
import type { ProjectMethodologyEnum } from 'gql/graphql';
import { useDebounce } from 'uikit';

import { useCallback, useEffect, useMemo } from 'react';
import { useDispatch } from 'react-redux';

import { createCatalogOtherCostVariableGroupGql, isUniqueVariableGroupGql } from '../CatalogVariableGroupQueries';

export const useCreateVariableGroup = ({
    groupName,
    removeInputHandler,
    updateGroupList,
    catalogUniqueId,
    isGlobal = false,
    methodology,
}: {
    groupName: string | null;
    removeInputHandler: () => void;
    updateGroupList: () => void;
    catalogUniqueId: string | undefined;
    isGlobal?: boolean;
    methodology: ProjectMethodologyEnum;
}) => {
    const dispatch = useDispatch();

    const memoizedRequestIds = useMemo(() => ['isUniqueVariableGroup', 'createCatalogOtherCostVariableGroup'], []);

    const [resetCreateData] = useResetMultipleStoreData({ requestIds: memoizedRequestIds });

    useEffect(
        () => () => {
            resetCreateData();
        },
        [resetCreateData],
    );

    const [isUniqueVariableGroupData, isUniqueVariableGroupStatus, , isUniqueVariableGroupRequest] = useRequest({
        requestId: 'isUniqueVariableGroup',
        query: isUniqueVariableGroupGql,
        withEffect: false,
    });

    const debounceRequest = useDebounce(() => {
        if (groupName && catalogUniqueId) {
            dispatch(
                isUniqueVariableGroupRequest({
                    groupName: groupName.trim(),
                    catalogUniqueId,
                    isGlobal,
                    methodology,
                }),
            );
        }
    }, 300);

    useEffect(() => {
        if (groupName?.length) {
            debounceRequest();
        }
    }, [debounceRequest, groupName?.length]);

    const tempSuccessHandler = useCallback(() => {
        removeInputHandler();
        updateGroupList();
        resetCreateData();
    }, [removeInputHandler, resetCreateData, updateGroupList]);

    const [, , , createOtherVariableGroupRequest] = useRequest({
        requestId: 'createCatalogOtherCostVariableGroup',
        query: createCatalogOtherCostVariableGroupGql,
        withEffect: false,
        dependenciesUpdater: tempSuccessHandler,
    });

    const createOtherVariableGroupHandler = useCallback(
        (name: string) => {
            if (name && catalogUniqueId) {
                dispatch(
                    createOtherVariableGroupRequest({
                        input: {
                            name,
                            catalogUniqueId,
                            isGlobal,
                            methodology,
                        },
                    }),
                );
            }
        },
        [catalogUniqueId, createOtherVariableGroupRequest, dispatch, isGlobal, methodology],
    );

    return useMemo(
        () => ({
            isUniqueVariableGroupData: isUniqueVariableGroupData?.isUniqueVariableGroup.result,
            isUniqueVariableGroupStatus,
            createOtherVariableGroupHandler,
        }),
        [
            createOtherVariableGroupHandler,
            isUniqueVariableGroupData?.isUniqueVariableGroup.result,
            isUniqueVariableGroupStatus,
        ],
    );
};
